import L from 'leaflet';
import { eventBus } from "@/main";
import { useFleetStore } from '@/stores/fleet';
import { createShipIcon } from '@/utils/shipIcon';
import { fetchShipAisData, resolveShipImageUrl } from '@/services/aisService';
import prettyMilliseconds from 'pretty-ms';

export class ShipsLayer {
    constructor(map, layerControl) {
        this.map = map;
        this.layerControl = layerControl;
        this.shipMarkersGroup = null;
        this.selectedShipId = null;
        this.routeLayer = null; // Слой для маршрута
        this.portsData = null; // Кэш данных о портах
    }

    addShipLayer() {
        if (!this.map) return;

        this.shipMarkersGroup = L.layerGroup().addTo(this.map);
    }

    // Отрисовка кораблей на карте
    drawShips(ships, selectedShipId = null, props = null) {
        if (!this.map) {
            console.error("Map not initialized for drawing ships");
            return;
        }

        const { onPopupClose } = props || {};
        // store currenty opened popup state and offset, to keep it visually unchanged when redrawing
        
        
        let popupOffset = [0, 0];
        let isExpandedDetails = false;

        // Очищаем предыдущие маркеры, если они есть
        if (this.shipMarkersGroup) {
            const selectedLayer = this.shipMarkersGroup.getLayers().find(layer => layer.shipDeviceId === selectedShipId);
            if (selectedLayer && selectedLayer.options.isOpennedIndicator) {
                popupOffset = selectedLayer.options.popupOffset ?? [0, 0];
                isExpandedDetails = selectedLayer.options.isExpandedDetails || false;
            }
            
            this.shipMarkersGroup.getLayers().forEach(layer => {
                layer.options.isClearing = true;                
            });
            
            this.shipMarkersGroup.clearLayers();

            // Добавляем группу маркеров на карту, если она еще не добавлена
            if (!this.map.hasLayer(this.shipMarkersGroup)) {
                this.shipMarkersGroup.addTo(this.map);
            }
        } else {
            // Создаем новую группу маркеров, если она еще не существует
            this.shipMarkersGroup = L.layerGroup().addTo(this.map);
        }

        if (!ships || ships.length === 0) {
            return;
        }

        // Создаем маркеры для каждого корабля
        ships.forEach(ship => {
            if (!ship.position || !ship.position.lat || !ship.position.lon) {
                return;
            }

            const currentPopupOffset = ship.value === selectedShipId ? popupOffset : [0, 0];

            const marker = L.marker([ship.position.lat, ship.position.lon], {
                icon: this.createShipIcon(ship),
                title: ship.name,
                ship: ship,
                isOpennedIndicator: false,
                popupOffset: currentPopupOffset,
                isExpandedDetails: isExpandedDetails
            });

            // Создаем попап с базовым содержимым
            const popup = L.popup({
                closeButton: true,
                className: 'ship-popup',
                maxWidth: 560,
                maxHeight: 500,
                offset: currentPopupOffset
            }).setContent('<div class="loading">Загрузка данных...</div>');

            marker.bindPopup(popup);
            this.enablePopupDrag(marker, popup);

            // Обработчик открытия попапа для загрузки дополнительных данных
            marker.on('popupopen', async (e) => {
                marker.options.isOpennedIndicator = true;                

                try {
                    const popupContent = await this.createShipPopupContent(
                        ship, 
                        isExpandedDetails
                    );
                    popup.setContent(popupContent);

                    this.initializePopupDetailsToggle(
                        popup,
                        (collapsed) => {
                            marker.options.isExpandedDetails = !collapsed;
                            console.log("isExpandedDetails", marker.options.isExpandedDetails);
                        }
                    );
                    
                    // Построить маршрут если есть данные о портах
                    const cacheData = await this.loadShipCacheData(ship.imo);
                    if (cacheData && cacheData.ports) {
                        // Проверяем формат данных портов
                        let portsArray;
                        if (Array.isArray(cacheData.ports)) {
                            // Массив портов (как обычно)
                            portsArray = cacheData.ports;
                        } else if (typeof cacheData.ports === 'object' && cacheData.ports.portSign) {
                            // Одиночный порт в виде объекта - преобразуем в массив
                            portsArray = [cacheData.ports];
                        }
                        
                        if (portsArray && portsArray.length > 0) {
                            await this.drawShipRoute(portsArray);
                        }
                    }
                } catch (error) {
                    console.error('Ошибка загрузки данных корабля:', error);
                    // Показываем базовые данные при ошибке
                    const basicContent = this.createBasicPopupContent(ship);
                    popup.setContent(basicContent);
                    this.initializePopupDetailsToggle(popup);
                }
            });
            
            // Обработчик закрытия попапа для очистки маршрута
            marker.on('popupclose', () => {
                if (!marker.options.isClearing) {
                    delete marker.options.popupOffset;
                    onPopupClose?.(ship);
                }
                this.clearRoute();
                marker.options.isOpennedIndicator = false;
            });

            // Сохраняем deviceId в маркере для дальнейшего использования
            marker.shipDeviceId = ship.value;

            // Обработка клика по маркеру
            marker.on('click', (e) => {
                L.DomEvent.stopPropagation(e);
                // Выбрать корабль в таблице
                eventBus.$emit('select-ship', ship.value);
            });

            this.shipMarkersGroup.addLayer(marker);

            if (ship.bearing !== null && ship.bearing !== undefined) {
                const bearingMarker = L.marker([ship.position.lat, ship.position.lon], {
                    icon: this.createShipBearingIcon(ship)
                });
                this.shipMarkersGroup.addLayer(bearingMarker);
            }

            // Если есть выбранный deviceId, открываем попап
            if (selectedShipId && ship.value === selectedShipId) {
                marker.openPopup();
            }
        });
    }

    enablePopupDrag(marker, popup) {
        if (!popup || typeof popup.on !== 'function') return;

        const onPopupAdd = () => {
            const container = popup.getElement();
            if (!container) return;

            const handle = container.querySelector('.leaflet-popup-content-wrapper');
            if (!handle) return;

            if (popup._shipDragCleanup) {
                popup._shipDragCleanup();
            }

            handle.style.cursor = 'move';

            const stopPropagation = event => {
                event.stopPropagation();
            };

            handle.addEventListener('pointerdown', stopPropagation, { passive: false });
            handle.addEventListener('pointermove', stopPropagation, { passive: false });

            const draggable = new L.Draggable(container, handle);
            //const draggable = new L.Draggable(popup._container, popup._wrapper);

            let mapDraggingWasEnabled = this.map && this.map.dragging && this.map.dragging.enabled();
            let dragStarted = false;

            const beforeDrag = () => {
                if (this.map && this.map.dragging && this.map.dragging.enabled()) {
                    mapDraggingWasEnabled = true;
                    this.map.dragging.disable();
                } else {
                    mapDraggingWasEnabled = false;
                }
                dragStarted = true;
                container.classList.add('ship-popup-dragging');
            };

            const afterDrag = (e) => {
                container.classList.remove('ship-popup-dragging');
                if (dragStarted && this.map && this.map.dragging && mapDraggingWasEnabled) {
                    this.map.dragging.enable();
                }
                dragStarted = false;

                if (e?.target?._startPos && e?.target?._newPos) {
                    const dx = e.target._newPos.x - e.target._startPos.x;
                    const dy = e.target._newPos.y - e.target._startPos.y;
                    if (!marker.options.popupOffset) {
                        marker.options.popupOffset = [0, 0];
                    }

                    marker.options.popupOffset[0] += dx;
                    marker.options.popupOffset[1] += dy;
                }
            };
            
            draggable.on('dragstart', beforeDrag);
            draggable.on('dragend', afterDrag);

            draggable.enable();

            popup._shipDragCleanup = () => {
                afterDrag();
                draggable.off('dragstart', beforeDrag);
                draggable.off('dragend', afterDrag);
                draggable.disable();
                handle.removeEventListener('pointerdown', stopPropagation);
                handle.removeEventListener('pointermove', stopPropagation);
                popup._shipDragCleanup = null;
            };
        };

        popup.on('add', onPopupAdd);

        popup.on('remove', () => {
            if (popup._shipDragCleanup) {
                popup._shipDragCleanup();
            }
        });
    }

    initializePopupDetailsToggle(popup, onToggleExpanded) {
        if (!popup || typeof popup.getElement !== 'function') {
            return;
        }

        const bindToggle = () => {
            const container = popup.getElement();
            if (!container) {
                return false;
            }

            const toggleButton = container.querySelector('.ship-popup-toggle');
            const detailsBlock = container.querySelector('.ship-popup-details');
            if (!toggleButton || !detailsBlock) {
                return false;
            }

            const updateButtonText = (collapsed) => {
                toggleButton.textContent = collapsed ? 'Show details' : 'Hide details';
                toggleButton.setAttribute('aria-expanded', (!collapsed).toString());
            };

            updateButtonText(detailsBlock.classList.contains('collapsed'));

            toggleButton.addEventListener('click', (event) => {
                event.stopPropagation();
                const collapsed = detailsBlock.classList.toggle('collapsed');
                onToggleExpanded?.(collapsed);

                if (collapsed) {
                    detailsBlock.setAttribute('aria-hidden', 'true');
                } else {
                    detailsBlock.removeAttribute('aria-hidden');
                }
                updateButtonText(collapsed);
            });

            return true;
        };

        if (!bindToggle()) {
            setTimeout(bindToggle, 0);
        }
    }

    createShipBearingIcon(ship) {
        const bearing = parseFloat(ship.bearing || 0);

        const svg = `
            <svg width="90" height="90" viewBox="0 0 90 90" xmlns="http://www.w3.org/2000/svg">
                <g transform="rotate(${bearing} 45 45)">
                    <line x1="45" y1="45" x2="45" y2="3" 
                          stroke="#ff8c00" 
                          stroke-width="4"
                          stroke-linecap="round"/>
                    <path d="M45 0 L50 10 L45 7 L40 10 Z" 
                          fill="#ff8c00" 
                          stroke="#cc6600" 
                          stroke-width="1.5"
                          stroke-linejoin="round"/>
                </g>
            </svg>
        `;

        return L.divIcon({
            className: 'ship-bearing-icon',
            html: svg,
            iconSize: [90, 90],
            iconAnchor: [45, 45],
        });
    }
    
    // Создание иконки корабля
    createShipIcon(ship) {
        const speed = parseFloat(ship.speed) || 0;
        const course = parseFloat(ship.aisCOG || 0);

        // Доступ к хранилищу для проверки предупреждений
        const fleetStore = useFleetStore();

        // Проверяем наличие предупреждений для Navarea
        const hasNavareaWarning = fleetStore.alerts &&
            fleetStore.alerts.navarea &&
            fleetStore.alerts.navarea[ship.value];

        // Проверяем наличие предупреждений о погоде
        const hasWeatherWarning = fleetStore.weather &&
            fleetStore.weather[ship.value] &&
            fleetStore.weather[ship.value].hasWarning;

        // Цвет иконки корабля в зависимости от скорости
        let iconColor = speed > 0 ? 'green' : 'grey';

        // Цвет если данные устарели
        if (ship.isOldData) {
            iconColor = 'yellow';
        }

        // Цвет если есть предупреждения Navarea или погоды
        if (hasNavareaWarning || hasWeatherWarning) {
            iconColor = 'red';
        }

        const colorMap = {
            green: { fill: '#22c55e', stroke: '#14532d' },
            grey: { fill: '#4b5563', stroke: '#e5e7eb' },
            yellow: { fill: '#f59e0b', stroke: '#78350f' },
            red: { fill: '#ef4444', stroke: '#7f1d1d' }
        };
        const { fill, stroke } = colorMap[iconColor] || colorMap.grey;

        return createShipIcon({
            width: 20,
            height: 40,
            headingDeg: course,
            scale: 0.72,
            fillColor: fill,
            strokeColor: stroke,
            strokeWidth: 1.4,
            className: 'ship-icon-container',
            alt: ship.name || 'Ship',
        });
    }

    // Форматирование значения поля (включая вложенные объекты)
    formatFieldValue(value, level = 0) {
        if (value === null || value === undefined || value === '' || value === '-') {
            return '';
        }

        if (Array.isArray(value)) {
            if (value.length === 0) {
                return '';
            }

            const renderedItems = value
                .map((item, index) => {
                    if (item === null || item === undefined || item === '' || item === '-') {
                        return '';
                    }

                    if (typeof item === 'object' && item !== null) {
                        const rows = Object.entries(item)
                            .map(([subKey, subValue]) => {
                                const formattedSub = this.formatFieldValue(subValue, level + 2);
                                if (!formattedSub) {
                                    return '';
                                }

                                const displaySubKey = this.getDisplayName(subKey);
                                return this.renderFieldRow(displaySubKey, formattedSub);
                            })
                            .filter(Boolean)
                            .join('');

                        if (!rows) {
                            return '';
                        }

                        const indexBadge = value.length > 1
                            ? `<div class="ship-popup-field-index">${index + 1}</div>`
                            : '';

                        return `
              <div class="ship-popup-field-group">
                ${indexBadge}
                <div class="ship-popup-field-group-body">
                  ${rows}
                </div>
              </div>
            `;
                    }

                    const text = String(item).trim();
                    if (!text) {
                        return '';
                    }

                    return `<div class="ship-popup-field-text">${text}</div>`;
                })
                .filter(Boolean)
                .join('');

            return renderedItems;
        }

        if (typeof value === 'object') {
            const rows = Object.entries(value)
                .map(([subKey, subValue]) => {
                    const formattedSub = this.formatFieldValue(subValue, level + 1);
                    if (!formattedSub) {
                        return '';
                    }

                    const displaySubKey = this.getDisplayName(subKey);
                    return this.renderFieldRow(displaySubKey, formattedSub);
                })
                .filter(Boolean)
                .join('');

            if (!rows) {
                return '';
            }

            return `
        <div class="ship-popup-field-group">
          <div class="ship-popup-field-group-body">
            ${rows}
          </div>
        </div>
      `;
        }

        const textValue = String(value).trim();
        if (!textValue || textValue === '-') {
            return '';
        }

        return `<span class="ship-popup-field-text">${textValue}</span>`;
    }

    // Получение отображаемого имени для поля
    getDisplayName(key) {
        const fieldNameMap = {
            'mmsi': 'MMSI',
            'vessel_type': 'Vessel type',
            'type_specific': 'Vessel specific',
            'build_year': 'Build year',
            'length': 'Length',
            'beam': 'Beam',
            'gross_tonnage': 'Tonnage',
            'deadweight': 'Deadweight',
            'navigational_status': 'Navigational status',
            'last_port': 'Last port',
            'eta_utc': 'ETA (UTC)',
            'atd_utc': 'ATD (UTC)',
            'current_draught': 'Current draught',
            'position_received': 'Position received',
            'unlocode_destination': 'Destination',
            'teu': 'TEU',
            'pi': 'PI',
            'fuel_type': 'Fuel',
            'engine_type': 'Type',
            'engine_builder': 'Builder',
            'engine_power_kw': 'Power (kw)',
            'ism': 'ISM',
            'ism_email': 'ISM email',
            'ism_website': 'ISM website',
            'ism_address': 'ISM address',
            'manager_email': 'Manager email',
            'manager_website': 'Manager website',
            'manager_address': 'Manager address',
            'registered_owner': 'Owner',
            'registered_owner_email': 'Owner email',
            'registered_owner_website': 'Owner website',
            'registered_owner_address': 'Owner address',
            'classification_society': 'Classification society'
        };
        
        return fieldNameMap[key.toLowerCase()] || (key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1'));
    }

    // Загрузка дополнительных данных корабля из удалённого сервиса AIS
    async loadShipCacheData(shipImo) {
        if (!shipImo) return null;

        try {
            const data = await fetchShipAisData(shipImo);
            return data || null;
        } catch (error) {
            console.log(`Удалённые данные для ${shipImo} не получены:`, error);
        }
        return null;
    }

    // Загрузка данных о портах
    async loadPortsData() {
        if (this.portsData) return this.portsData;
        
        try {
            const response = await fetch('/ports.json');
            if (response.ok) {
                const portsArray = await response.json();
                // Преобразуем массив в объект для удобного поиска по port_code
                this.portsData = {};
                if (Array.isArray(portsArray)) {
                    portsArray.forEach(port => {
                        if (port.port_code && port.lat && port.lon) {
                            // Если порт с таким кодом уже есть, берём первый (обычно основной)
                            if (!this.portsData[port.port_code]) {
                                this.portsData[port.port_code] = {
                                    name: port.name,
                                    country: port.country,
                                    coordinates: [port.lon, port.lat] // [longitude, latitude] как в оригинальном формате
                                };
                            }
                        }
                    });
                }
                return this.portsData;
            }
        } catch (error) {
            console.log('Ошибка загрузки данных о портах:', error);
        }
        return null;
    }

    // Группировка последовательных визитов в один порт
    groupPortVisits(ports) {
        if (!ports || ports.length === 0) return [];
        
        // Если только один порт, возвращаем его как группу
        if (ports.length === 1) {
            const port = ports[0];
            return [{
                portSign: port.portSign,
                portName: port.portName,
                firstArrived: port.arrived,
                lastDeparted: port.departed,
                visits: [port],
                originalIndex: 0
            }];
        }
        
        // Сначала разворачиваем массив для обработки в хронологическом порядке
        const reversedPorts = [...ports].reverse();
        
        const grouped = [];
        let currentGroup = null;
        
        reversedPorts.forEach((port, index) => {
            if (!currentGroup || currentGroup.portSign !== port.portSign) {
                // Начинаем новую группу
                if (currentGroup) {
                    grouped.push(currentGroup);
                }
                currentGroup = {
                    portSign: port.portSign,
                    portName: port.portName,
                    firstArrived: port.arrived, // Дата прибытия из первого визита (в хронологическом порядке)
                    lastDeparted: port.departed, // Дата отправления из первого визита (будет обновлена)
                    visits: [port],
                    originalIndex: ports.length - 1 - index // Преобразуем индекс обратно к исходному массиву
                };
            } else {
                // Продолжаем текущую группу
                currentGroup.visits.push(port);
                // Обновляем дату отъезда на дату отъезда из текущего (последнего в хронологической последовательности) визита
                if (port.departed) {
                    currentGroup.lastDeparted = port.departed;
                }
            }
        });
        
        // Добавляем последнюю группу
        if (currentGroup) {
            grouped.push(currentGroup);
        }
        
        // Возвращаем в исходном порядке (последние порты первые)
        return grouped.reverse();
    }

    // Построение маршрута по портам
    async drawShipRoute(ports) {
        if (!ports || !Array.isArray(ports) || ports.length === 0) return;
        
        const portsData = await this.loadPortsData();
        if (!portsData) return;
        
        // Очищаем предыдущий маршрут
        this.clearRoute();
        
        // Создаем слой для маршрута
        this.routeLayer = L.layerGroup().addTo(this.map);
        
        // Группируем последовательные визиты
        const groupedPorts = this.groupPortVisits(ports);
        const routePoints = [];
        
        // Проходим по сгруппированным портам
        groupedPorts.forEach((portGroup, index) => {
            const portInfo = portsData[portGroup.portSign];
            if (portInfo && portInfo.coordinates) {
                const [lng, lat] = portInfo.coordinates;
                const portLatLng = L.latLng(lat, lng);
                routePoints.push(portLatLng);
                
                // Создаем маркер порта
                const portMarker = L.circleMarker(portLatLng, {
                    radius: 6,
                    fillColor: index === 0 ? '#ff0000' : '#0066cc', // Красный для последнего, синий для остальных
                    color: '#ffffff',
                    weight: 2,
                    opacity: 1,
                    fillOpacity: 0.8
                });
                
                // Определяем размер текста для вычисления размера фона
                const portName = portInfo.name;
                
                // Формируем текст с датами для тултипа
                let dateText;
                if (portGroup.visits.length === 1) {
                    // Один визит
                    const visit = portGroup.visits[0];
                    if (visit.departed) {
                        dateText = `${visit.arrived} - ${visit.departed}`;
                    } else {
                        dateText = `${visit.arrived} (current)`;
                    }
                } else {
                    // Несколько визитов
                    dateText = `${portGroup.firstArrived} - ${portGroup.lastDeparted || 'current'}`;
                }
                
                const textWidth = Math.max(portName.length * 7, dateText.length * 6, 100); // Учитываем обе строки
                
                // Создаем подпись порта с правильным размером фона и датами
                const portLabel = L.divIcon({
                    className: 'port-label-custom',
                    html: `<div style="
                        background: rgba(255,255,255,0.95); 
                        padding: 4px 8px; 
                        border-radius: 4px; 
                        font-size: 11px; 
                        font-weight: bold; 
                        color: #333; 
                        border: 1px solid #ccc;
                        box-shadow: 0 2px 4px rgba(0,0,0,0.2);
                        white-space: nowrap;
                        text-align: center;
                        min-width: ${textWidth}px;
                        line-height: 1.2;
                    ">
                        <div>${portName}</div>
                        <div style="font-size: 9px; font-weight: normal; color: #666; margin-top: 2px;">${dateText}</div>
                    </div>`,
                    iconSize: [textWidth + 16, 32],
                    iconAnchor: [(textWidth + 16) / 2, -5]
                });
                
                const labelMarker = L.marker(portLatLng, { icon: portLabel });
                
                // Формируем текст о посещении порта
                let visitText;
                if (portGroup.visits.length === 1) {
                    // Один визит
                    const visit = portGroup.visits[0];
                    visitText = `<strong>Visit:</strong> ${visit.arrived}${visit.departed ? ` - ${visit.departed}` : ' (still in port)'}`;
                } else {
                    // Несколько визитов
                    visitText = `<strong>Multiple visits (${portGroup.visits.length}):</strong><br>
                        ${portGroup.firstArrived} - ${portGroup.lastDeparted || 'still in port'}`;
                }
                
                // Добавляем попап с информацией о порте
                const popupContent = `
                    <div style="min-width: 220px; max-width: 300px;">
                        <h6 style="margin: 0 0 8px 0; color: #2c3e50;">${portInfo.name}</h6>
                        <p style="margin: 4px 0;"><strong>Code:</strong> ${portGroup.portSign}</p>
                        <p style="margin: 4px 0;">${visitText}</p>
                        ${portInfo.country ? `<p style="margin: 4px 0;"><strong>Country:</strong> ${portInfo.country}</p>` : ''}
                    </div>
                `;
                portMarker.bindPopup(popupContent);
                
                this.routeLayer.addLayer(portMarker);
                this.routeLayer.addLayer(labelMarker);
            }
        });
        
        // Рисуем линию маршрута
        if (routePoints.length > 1) {
            const routeLine = L.polyline(routePoints, {
                color: '#0066cc',
                weight: 3,
                opacity: 0.7,
                dashArray: '5, 10'
            });
            this.routeLayer.addLayer(routeLine);
        }
    }

    // Очистка маршрута
    clearRoute() {
        if (this.routeLayer) {
            if (this.map && this.map.hasLayer && this.map.hasLayer(this.routeLayer)) {
                this.map.removeLayer(this.routeLayer);
            }
            this.routeLayer.clearLayers && this.routeLayer.clearLayers();
            this.routeLayer = null;
        }
    }

        // Создание контента для попапа корабля
        async createShipPopupContent(ship, isExpandedDetails = false) {
                const statusVariant = ship.aisStatusVariant || 'secondary';
                const statusLabel = ship.aisStatus || '';

                const cacheData = await this.loadShipCacheData(ship.imo);
                const imageUrl = resolveShipImageUrl(cacheData);

                const infoRows = this.buildPrimaryInfoRows(ship);
                const additionalSections = this.buildAdditionalSections(cacheData);

                return this.renderShipPopup(ship, {
                        statusLabel,
                        statusVariant,
                        imageUrl,
                        infoRows,
                        sections: additionalSections,
                        isExpandedDetails
                });
        }

        // Создание базового контента для попапа (без дополнительных данных)
        createBasicPopupContent(ship) {
                const statusVariant = ship.aisStatusVariant || 'secondary';
                const statusLabel = ship.aisStatus || '';
                const infoRows = this.buildPrimaryInfoRows(ship);

                return this.renderShipPopup(ship, {
                        statusLabel,
                        statusVariant,
                        imageUrl: null,
                        infoRows,
                        sections: ''
                });
        }

        buildPrimaryInfoRows(ship) {
            const rows = [];

            const imoValue = this.formatFieldValue(ship.imo || 'N/A');
            if (imoValue) {
                rows.push(this.renderInfoRow('IMO', imoValue));
            }

            if (ship.position && typeof ship.position.lat === 'number' && typeof ship.position.lon === 'number') {
                const coords = `${ship.position.lat.toFixed(5)}, ${ship.position.lon.toFixed(5)}`;
                const coordsValue = this.formatFieldValue(coords);
                if (coordsValue) {
                    rows.push(this.renderInfoRow('Position', coordsValue));
                }
            }

            if (ship.heading !== null && ship.heading !== undefined) {
                rows.push(this.renderInfoRow('Heading', ship.heading.toString() + '°'));
            }

            if (ship.bearing !== null && ship.bearing !== undefined) {
                rows.push(this.renderInfoRow('Bearing', ship.bearing.toString() + '°'));
            }

            if (ship.rot !== null && ship.rot !== undefined) {
                rows.push(this.renderInfoRow('Rot', (Math.round(ship.rot * 100) / 100).toString() + '° / sec'));
            }

            if (ship.rudder !== null && ship.rudder !== undefined) {
                rows.push(this.renderInfoRow('Rudder', ship.rudder.toString()));
            }

            if (ship.speed !== null && ship.speed !== undefined && ship.speed !== '') {
                const numericSpeed = Number(ship.speed);
                const speedValue = Number.isFinite(numericSpeed)
                    ? `${numericSpeed.toFixed(1)} kn`
                    : `${ship.speed} kn`;
                const formattedSpeed = this.formatFieldValue(speedValue);
                if (formattedSpeed) {
                    rows.push(this.renderInfoRow('Speed', formattedSpeed));
                }
            }

            if (ship.destination) {
                const destinationValue = this.formatFieldValue(ship.destination);
                if (destinationValue) {
                    rows.push(this.renderInfoRow('Destination', destinationValue));
                }
            }

            if (ship.eta) {
                const etaValue = this.formatFieldValue(this.formatPopupDate(ship.eta));
                if (etaValue) {
                    rows.push(this.renderInfoRow('ETA', etaValue));
                }
            }

            if (ship.timeStamp !== null && ship.timeStamp !== undefined && ship.timeStamp !== '') {
                const timeStampValue = this.formatFieldValue(this.formatPopupDate(ship.timeStamp));
                if (timeStampValue) {
                    rows.push(this.renderInfoRow('Time Stamp', timeStampValue));
                    if (ship.showLastUpdateTimeDiff) {
                        const timeDiff = prettyMilliseconds(Date.now() - new Date(ship.timeStamp).getTime(), { compact: true });
                        rows.push(this.renderInfoRow('Updated', timeDiff + ' ago'));
                    }
                }
            }

            return rows.filter(Boolean).join('');
        }

        buildAdditionalSections(cacheData) {
            if (!cacheData) {
                return '';
            }

            const fieldsWithSeparator = ['engine', 'management'];
            const constructionFields = ['build_year', 'length', 'beam', 'gross_tonnage', 'deadweight'];
            const excludeFields = [
                'name', 'imo', 'position', 'speed', 'destination', 'eta', 'image_url', 'has_image',
                'api_update_time', 'data_source', 'ports', 'country_iso', 'unlocode_lastport',
                'api_location_update_time', 'location_last_update', 'cache_update_time', 'build_year',
                'length', 'beam', 'gross_tonnage', 'deadweight', 'image_full_url', 'imoNumeric',
                'eta_iso', 'position_received_iso', 'update_time', 'update_time_iso', 'weather', 'raw'
            ];

            const separatorSet = new Set(fieldsWithSeparator.map(field => field.toLowerCase()));
            const constructionSet = new Set(constructionFields.map(field => field.toLowerCase()));
            const excludeSet = new Set(excludeFields.map(field => field.toLowerCase()));

            const lowerCaseMap = new Map();
            Object.entries(cacheData).forEach(([key, value]) => {
                lowerCaseMap.set(key.toLowerCase(), { key, value });
            });

            const sections = [];
            const generalRows = [];

            lowerCaseMap.forEach(({ key, value }, lowerKey) => {
                if (excludeSet.has(lowerKey) || separatorSet.has(lowerKey) || constructionSet.has(lowerKey)) {
                    return;
                }

                const formattedValue = this.formatFieldValue(value);
                if (!formattedValue) {
                    return;
                }

                const displayKey = this.getDisplayName(key);
                generalRows.push(this.renderInfoRow(displayKey, formattedValue));
            });

            if (generalRows.length) {
                sections.push(this.renderSection('Additional Details', generalRows.join('')));
            }

            const constructionRows = [];
            constructionFields.forEach(fieldKey => {
                const entry = lowerCaseMap.get(fieldKey.toLowerCase());
                if (!entry) {
                    return;
                }

                const formattedValue = this.formatFieldValue(entry.value);
                if (!formattedValue) {
                    return;
                }

                const displayKey = this.getDisplayName(entry.key);
                constructionRows.push(this.renderInfoRow(displayKey, formattedValue));
            });

            if (constructionRows.length) {
                sections.push(this.renderSection('Construction', constructionRows.join('')));
            }

            fieldsWithSeparator.forEach(fieldKey => {
                const entry = lowerCaseMap.get(fieldKey.toLowerCase());
                if (!entry) {
                    return;
                }

                const formattedValue = this.formatFieldValue(entry.value);
                if (!formattedValue) {
                    return;
                }

                const displayKey = this.getDisplayName(entry.key);
                sections.push(this.renderSection(displayKey, formattedValue));
            });

            return sections.join('');
        }

        renderShipPopup(ship, { statusLabel, statusVariant, imageUrl, infoRows, sections, isExpandedDetails }) {
            const variantClass = (statusVariant || 'secondary').toString().toLowerCase();
            const statusBadge = statusLabel
                ? `<span class="ship-popup-status ship-popup-status-${variantClass}">${statusLabel}</span>`
                : '';

            const imageBlock = imageUrl
                ? `<div class="ship-popup-image"><img src="${imageUrl}" alt="Ship image"></div>`
                : '';

                        const infoBlock = infoRows ? `<div class="ship-popup-info-grid">${infoRows}</div>` : '';
                        const sectionsBlock = sections || '';
                        const hasDetails = Boolean(infoBlock || sectionsBlock);
                        const toggleControls = hasDetails
                                            ? `<div class="ship-popup-details-container">
                                                     <button type="button" class="ship-popup-toggle" aria-expanded="false">${isExpandedDetails ? 'Hide' : 'Show'} details</button>
                                                     <div class="ship-popup-details ${(isExpandedDetails ? '' : 'collapsed')}" aria-hidden="true">
                                                         ${infoBlock}${sectionsBlock}
                                                     </div>
                                                 </div>`
                                : `${infoBlock}${sectionsBlock}`;

                        return `
                    <div class="ship-popup-content scrollable-content">
                        <div class="ship-popup-card">
                            <div class="ship-popup-header">
                                <div class="ship-popup-header-info">
                                    <div class="ship-popup-label">Selected vessel</div>
                                    <div class="ship-popup-title">${ship.name || 'Unknown vessel'}</div>
                                </div>
                                ${statusBadge}
                            </div>
                            ${imageBlock}
                            ${toggleControls}
                        </div>
                    </div>
                `;
        }

        renderInfoRow(label, valueHtml) {
            if (!valueHtml) {
                return '';
            }

            return `
          <div class="ship-popup-info-row">
            <div class="ship-popup-info-label">${label}</div>
            <div class="ship-popup-info-value">${valueHtml}</div>
          </div>
        `;
        }

        renderSection(title, bodyHtml) {
            if (!bodyHtml) {
                return '';
            }

            return `
          <div class="ship-popup-section">
            <div class="ship-popup-section-title">${title}</div>
            <div class="ship-popup-section-body">
              ${bodyHtml}
            </div>
          </div>
        `;
        }

        renderFieldRow(label, valueHtml) {
            if (!valueHtml) {
                return '';
            }

            return `
          <div class="ship-popup-field-row">
            <div class="ship-popup-field-sub-label">${label}</div>
            <div class="ship-popup-field-sub-value">${valueHtml}</div>
          </div>
        `;
        }

        formatPopupDate(value) {
            try {
                const date = new Date(value);
                if (Number.isNaN(date.getTime())) {
                    return String(value);
                }
                return date.toLocaleString();
            } catch (error) {
                return String(value);
            }
        }

    // Отцентрировать карту на выбранном корабле и показать попап
    setSelectedShip(deviceId, zoom = 8) {
        this.selectedShipId = deviceId;

        if (this.shipMarkersGroup && deviceId) {
            const layers = this.shipMarkersGroup.getLayers();
            for (const layer of layers) {
                if (layer.shipDeviceId === deviceId) {
                    console.log("Zoom:", this.map.getZoom());

                    this.map.setView(layer.getLatLng(), Math.max(this.map.getZoom(), zoom));
                    layer.openPopup();
                    break;
                }
            }
        }
    }

    // Удалить все маркеры
    destroy() {
        // Очищаем маршрут
        this.clearRoute();
        
        if (this.shipMarkersGroup) {
            if (this.map && this.map.hasLayer && this.map.hasLayer(this.shipMarkersGroup)) {
                this.map.removeLayer(this.shipMarkersGroup);
            }
            this.shipMarkersGroup.clearLayers && this.shipMarkersGroup.clearLayers();
            this.shipMarkersGroup = null;
        }

        this.selectedShipId = null;
        this.portsData = null;
    }
}